import { ChartBar, ChartRangeType, DropdownWithStatistic } from "components";
import { t as translate } from "i18next";
import { useTranslation } from "react-i18next";
import { Loader, Stat } from "ui";

import {
  FinancialSummaryResponse,
  RevenueBreakdownResponse,
  RevenueByVariant
} from "../../../apis";
import { CategoryBreakdown, SectionCard } from "../atoms";
import {
  formatPercent,
  formatXof,
  getTotalsMetricDelta
} from "../financialFormat";

import styles from "./ProfitLossSection.module.scss";

interface ProfitLossSectionProps {
  data: RevenueBreakdownResponse | undefined;
  summary: FinancialSummaryResponse | undefined;
  isLoading: boolean;
  showComparison: boolean;
  range: [Date, Date];
  activeRangeType: ChartRangeType;
}

const renderProductBadge = (variant: RevenueByVariant) => (
  <div className={styles.productMeta}>
    <div className={styles.productMeta__margin}>
      {translate("Margin")}{" "}
      <strong>{formatPercent(variant.marginPercent, 0)}</strong>
    </div>

    {variant.adCost > 0 && (
      <div className={styles.productMeta__adChip}>
        {translate("Profit after ad spend")}{" "}
        <strong>{formatXof(variant.grossProfit - variant.adCost)}</strong>
        <span className={styles.productMeta__adCost}>
          − {formatXof(variant.adCost)}
        </span>
      </div>
    )}
  </div>
);

const ProfitLossSection = ({
  data,
  summary,
  isLoading,
  showComparison,
  range,
  activeRangeType
}: ProfitLossSectionProps) => {
  const { t } = useTranslation();

  if (!data) {
    return (
      <SectionCard title="Profit and loss">
        <Loader />
      </SectionCard>
    );
  }

  const grossProfitDelta = getTotalsMetricDelta(
    data.totals,
    data.previousTotals,
    data.deltas,
    "grossProfit"
  );

  return (
    <SectionCard title="Profit and loss">
      <div className={styles.topBlock}>
        <div className={styles.totals}>
          <Stat
            size="lg"
            title="Gross profit · revenue minus cost of goods"
            amount={formatXof(data.totals.grossProfit)}
            percentage={
              showComparison ? (grossProfitDelta ?? undefined) : undefined
            }
          />
          <Stat
            size="lg"
            title="Net profit · after other costs and advertising"
            amount={formatXof(summary?.netProfit ?? 0)}
          />

          {data.lowMarginCount > 0 && (
            <span className={styles.lowMarginPill}>
              {data.lowMarginCount}{" "}
              {t(
                data.lowMarginCount === 1
                  ? "product under {{threshold}}% margin"
                  : "products under {{threshold}}% margin",
                { threshold: data.lowMarginThreshold }
              )}
            </span>
          )}
        </div>

        <div className={styles.chartWrap}>
          <ChartBar
            datepicker={null}
            activeRangeType={activeRangeType}
            onRangeTypeChange={() => {}}
            dateRange={range}
            isLoading={isLoading}
            data={data.series.map((point) => ({
              date: point.date,
              // The revenue-breakdown endpoint leaves grossProfit null on each series
              // point (only totals.grossProfit is populated) — derive it the same way
              // the API documents grossProfit itself: netRevenue - costOfGoods.
              value: point.netRevenue - point.costOfGoods
            }))}
          />
        </div>
      </div>

      <DropdownWithStatistic label="Profit by category" defaultOpen>
        <CategoryBreakdown
          level1={data.byLevel1}
          level2={data.byLevel2}
          variants={data.byVariant}
          metric="grossProfit"
          totalValue={data.totals.grossProfit}
          showComparison={showComparison}
          shareLabel="of period profit"
          productsLabel="Profit by product"
          renderProductBadge={renderProductBadge}
        />
      </DropdownWithStatistic>
    </SectionCard>
  );
};

export default ProfitLossSection;
